import React, { useState, useEffect } from 'react';
import { HardDrive, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StorageIndicatorHorizontal = ({ className = "", refreshTrigger = 0 }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchStorageData();
    }
  }, [user, refreshTrigger]);

  const fetchStorageData = async () => {
    try {
      // Try with user.id first, if that fails try with user.email
      const identifier = user.id || user.email;
      const timestamp = new Date().getTime(); // Cache buster
      const response = await fetch(`${BACKEND_URL}/api/researchers/storage/${identifier}?_=${timestamp}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Storage API Response:', data);
        console.log('Library documents count:', data.library_documents_count);
        setStorageData(data);
      } else if (user.email && user.id !== user.email) {
        // If failed with id, try with email
        const emailResponse = await fetch(`${BACKEND_URL}/api/researchers/storage/${user.email}?_=${timestamp}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        if (emailResponse.ok) {
          const data = await emailResponse.json();
          setStorageData(data);
        }
      }
    } catch (error) {
      console.error('Error fetching storage data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div className="text-sm text-gray-500">Loading storage...</div>
      </div>
    );
  }

  if (!storageData) {
    return (
      <div className="text-xs text-red-500">
        Storage data not loaded
      </div>
    );
  }

  // Helper function to translate storage units
  const translateStorageUnit = (text) => {
    if (!text) return text;
    return text
      .replace(/\sGB/g, ` ${t('gb')}`)
      .replace(/\sMB/g, ` ${t('mb')}`)
      .replace(/\sKB/g, ` ${t('kb')}`)
      .replace(/\sBytes/g, ` ${t('bytes')}`);
  };

  const percentage = storageData.storage_percentage;
  const isWarning = percentage > 70;
  const isCritical = percentage > 90;

  return (
    <div className={`flex items-center gap-6 ${className}`}>
      {/* Icon and Title */}
      <div className="flex items-center gap-2">
        <HardDrive className="h-5 w-5 text-gray-600" />
        <span className="text-sm font-semibold text-gray-700">{t('storage')}:</span>
      </div>

      {/* Storage Bar and Info */}
      <div className="flex-1 flex items-center gap-4">
        {/* Usage Text */}
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-900">
            {translateStorageUnit(storageData.total_storage_display)}
          </span>
          <span className="text-gray-500">/</span>
          <span className="text-gray-600">
            {translateStorageUnit(storageData.storage_limit_display)}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="flex-1 max-w-md">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                isCritical ? 'bg-red-600' :
                isWarning ? 'bg-yellow-500' :
                'bg-green-600'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Percentage */}
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${
            isCritical ? 'text-red-600' :
            isWarning ? 'text-yellow-600' :
            'text-green-600'
          }`}>
            {percentage.toFixed(1)}%
          </span>
          {isCritical && (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
        </div>

        {/* Plan Badge */}
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
          storageData.subscription_plan === 'advanced' ? 'bg-purple-100 text-purple-800' :
          storageData.subscription_plan === 'premium' ? 'bg-green-100 text-green-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {storageData.subscription_plan === 'advanced' ? t('advancedPlan') :
           storageData.subscription_plan === 'premium' ? t('premiumPlan') :
           t('basicPlan')}
        </span>
      </div>

      {/* Breakdown (compact) */}
      <div className="flex items-center gap-3 text-xs text-gray-600">
        <span title={`Library docs: ${storageData.library_documents_count}, User: ${user?.email}`}>
          📚 {storageData.library_documents_count || 0} docs
        </span>
        <span>📁 {storageData.project_files_count || 0} files</span>
      </div>
    </div>
  );
};

export default StorageIndicatorHorizontal;
